import React, { useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View, ScrollView } from 'react-native';
import Svg, { Circle, Line } from 'react-native-svg';
import { FontFamily } from '../../../../../constants/fonts';
import { CrewingHorizontalScrollView } from './webScrollStyles';

export interface ExecutionTrendData {
  labels: string[];
  // Secondary sub-label under each x-axis tick (e.g. "'26"), matching Scheduling Trend's ticks.
  labelSubLabels?: (string | undefined)[];
  kcphIndexPct: number[];
  adoptionPct: number[];
}

interface ExecutionTrendCardProps {
  title: string;
  subtitle?: string;
  data: ExecutionTrendData;
}

const MIN_H = 190;
const MIN_LABEL_WIDTH = 80;
// Reserved so the x-axis label row always has room within the fixed (non-scrolling) viewport.
const X_AXIS_ROW_HEIGHT = Platform.OS === 'web' ? 70 : 52; // labels + sub-label + classic Windows scrollbar
const TOOLTIP_GAP = 8;
const TOOLTIP_ESTIMATED_HEIGHT = 30;
// Keeps the first/last point circles fully inside the SVG instead of getting clipped in half at the edges.
const POINT_INSET = 6;

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const getTooltipWidth = (value: string) => Math.max(40, value.length * 7 + 16);

const ExecutionTrendCard: React.FC<ExecutionTrendCardProps> = ({ title, subtitle, data }) => {
  // Check for no data condition
  const hasData = data.labels.length > 0 && 
    (data.kcphIndexPct.length > 0 || data.adoptionPct.length > 0);
  
  if (!hasData) {
    return (
      <View style={styles.card}>
        <View style={styles.headerRow}>
          <View style={styles.titleRow}>
            <Text style={styles.title}>{title}</Text>
            {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
          </View>
        </View>
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>No execution data available</Text>
        </View>
      </View>
    );
  }

  const [w, setW] = useState(440);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [selectedPoint, setSelectedPoint] = useState<{ lineKey: string; index: number } | null>(null);
  // Fill whatever vertical space the card actually has, minus room for the x-axis label row below the plot.
  const H = Math.max(MIN_H, viewportHeight - X_AXIS_ROW_HEIGHT);
  const count = Math.max(1, data.labels.length);
  const ChartContainer: React.ElementType = Platform.OS === 'web' ? View : ScrollView;
  
  // Calculate dynamic Y-axis range based on data
  const allValues = [data.kcphIndexPct, data.adoptionPct].flat().filter((v) => typeof v === 'number' && !isNaN(v));
  const dataMin = allValues.length > 0 ? Math.min(...allValues) : 0;
  const dataMax = allValues.length > 0 ? Math.max(...allValues) : 100;
  const dataRange = Math.max(1, dataMax - dataMin);
  const padding = Math.max(18, dataRange * 0.2); // extra headroom so the value labels drawn above/below each point don't clip
  
  const MIN = Math.max(0, Math.floor((dataMin - padding) / 10) * 10); // Round down to nearest 10, minimum 0
  const MAX = Math.ceil((dataMax + padding) / 10) * 10; // Round up to nearest 10
  
  const xAt = (i: number) => (i + 0.5) * (w / count);
  const yAt = (v: number) => POINT_INSET + ((MAX - clamp(v, MIN, MAX)) / (MAX - MIN)) * (H - POINT_INSET * 2);
  const getTooltipLeft = (pointX: number, tooltipWidth: number) => {
    const hitLeft = pointX - 14;
    return clamp(14 - tooltipWidth / 2, 4 - hitLeft, w - tooltipWidth - 4 - hitLeft);
  };
  // Same 5 evenly spaced gridlines as the other Crewing charts; labels mirror them top-to-bottom.
  const yTicks = Array.from({ length: 5 }, (_, i) => MAX - (i * (MAX - MIN)) / 4);

  const lines = useMemo(
    () => [
      { key: 'kcph', label: 'KCPH Index %', color: '#7A5AF8', values: data.kcphIndexPct },
      { key: 'adoption', label: 'Crewing Tool Adoption %', color: '#2AA5E6', values: data.adoptionPct },
    ] as const,
    [data.kcphIndexPct, data.adoptionPct],
  );

  return (
    <View style={styles.card}>
      <View style={styles.headerRow}>
        <View style={styles.titleRow}>
          <Text style={styles.title}>{title}</Text>
          {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
        </View>
      </View>

      <View style={styles.legendRow}>
        {lines.map((line) => (
          <View key={line.key} style={styles.legendItem}>
            <View style={[styles.legendStroke, { backgroundColor: line.color }]} />
            <Text style={styles.legendText}>{line.label}</Text>
          </View>
        ))}
      </View>

      <View
        style={[styles.plotViewport, { minHeight: MIN_H + X_AXIS_ROW_HEIGHT }]}
        onLayout={(e) => {
          const nextHeight = e.nativeEvent.layout.height;
          if (nextHeight > 0 && Math.abs(nextHeight - viewportHeight) > 1) setViewportHeight(nextHeight);
        }}
      >
        <ChartContainer {...(Platform.OS === 'web' ? {} : { style: { flex: 1 }, contentContainerStyle: { flexGrow: 1 }, showsVerticalScrollIndicator: false, scrollEnabled: false })}>
          <View
            style={styles.plotWrap}
            onLayout={(e) => {
              const containerWidth = e.nativeEvent.layout.width;
              const minNeeded = count * MIN_LABEL_WIDTH;
              const next = Math.max(260, Math.max(minNeeded, containerWidth - 34));
              if (Math.abs(next - w) > 1) setW(next);
            }}
          >
            <View style={[styles.yAxis, { height: H }]}>
              {yTicks.map((t) => (
                <Text key={t} style={styles.yTick}>{`${Math.round(t)}%`}</Text>
              ))}
            </View>
            <CrewingHorizontalScrollView
              horizontal
              style={{ height: H + X_AXIS_ROW_HEIGHT }}
              contentContainerStyle={{ width: w }}
              showsHorizontalScrollIndicator={Platform.OS === 'web'}
            >
              <View style={{ width: w }}>
                <View style={[styles.plotArea, { height: H, width: w }]}> 
                    <Svg width={w} height={H}>
                      {yTicks.map((tick) => (
                        <Line key={tick} x1={0} y1={yAt(tick)} x2={w} y2={yAt(tick)} stroke="#E9EFF8" strokeWidth={1} />
                      ))}

                      {lines.map((line) =>
                        line.values.slice(1).map((value, index) => (
                          <Line
                            key={`${line.key}-${index}`}
                            x1={xAt(index)}
                            y1={yAt(line.values[index])}
                            x2={xAt(index + 1)}
                            y2={yAt(value)}
                            stroke={line.color}
                            strokeWidth={2}
                          />
                        )),
                      )}

                      {lines.map((line) =>
                        line.values.map((value, index) => (
                          <Circle
                            key={`${line.key}-pt-${index}`}
                            cx={xAt(index)}
                            cy={yAt(value)}
                            r={3.3}
                            fill="#FFFFFF"
                            stroke={line.color}
                            strokeWidth={2}
                          />
                        )),
                      )}

                    </Svg>
                    {lines.map((line) => line.values.map((value, index) => {
                      const pointX = xAt(index);
                      const pointY = yAt(value ?? 0);
                      const showAbove = pointY >= TOOLTIP_ESTIMATED_HEIGHT + TOOLTIP_GAP + 14;
                      const isSelected = selectedPoint?.lineKey === line.key && selectedPoint.index === index;
                      const tooltipValue = `${(value ?? 0).toFixed(1)}%`;
                      const tooltipWidth = getTooltipWidth(tooltipValue);
                      return (
                        <Pressable
                          key={`${line.key}-${index}`}
                          onPress={() => setSelectedPoint((previous) => isSelected ? null : { lineKey: line.key, index })}
                          onHoverIn={() => setSelectedPoint({ lineKey: line.key, index })}
                          onHoverOut={() => setSelectedPoint((previous) => previous?.lineKey === line.key && previous.index === index ? null : previous)}
                          style={[styles.pointHitArea, { left: pointX - 14, top: Math.max(0, pointY - 14) }]}
                        >
                          {isSelected ? (
                            <View
                              style={[
                                styles.tooltip,
                                { width: tooltipWidth, left: getTooltipLeft(pointX, tooltipWidth), ...(showAbove ? { bottom: 22 + TOOLTIP_GAP } : { top: 22 + TOOLTIP_GAP }) },
                              ]}
                            >
                              <Text style={styles.tooltipRow}>{tooltipValue}</Text>
                            </View>
                          ) : null}
                        </Pressable>
                      );
                    }))}
                  </View>

                <View style={[styles.xRow, { width: w }]}> 
                  {data.labels.map((label, idx) => (
                    <View
                      key={`${label}-${idx}`}
                      style={[styles.xLabelCell, { width: w / count }]}
                    >
                      <Text style={styles.xLabel}>{label}</Text>
                      {data.labelSubLabels?.[idx] ? (
                        <Text style={styles.xLabelSub}>{data.labelSubLabels[idx]}</Text>
                      ) : null}
                    </View>
                  ))}
                </View>
              </View>
            </CrewingHorizontalScrollView>
          </View>
        </ChartContainer>
      </View>

    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 12,
    flex: 1,
    minHeight: 360,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    flexWrap: 'wrap',
    width: '100%',
  },
  title: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1D21',
  },
  titleRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: 4,
    width: '100%',
  },
  plotArea: {
    position: 'relative',
  },
  pointHitArea: {
    position: 'absolute',
    top: 0,
    width: 28,
    height: 28,
    zIndex: 2,
  },
  tooltip: {
    position: 'absolute',
    left: -54,
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 4,
    backgroundColor: '#1E2127',
    zIndex: 3,
  },
  tooltipTitle: {
    marginBottom: 2,
    fontSize: 11,
    fontFamily: FontFamily.bold,
    color: '#FFFFFF',
  },
  tooltipRow: {
    fontSize: 10,
    fontFamily: FontFamily.regular,
    color: '#FFFFFF',
    fontWeight: '700',
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#838383',
  },
  legendRow: {
    marginTop: 6,
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 10,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E3E9F3',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendStroke: {
    width: 12,
    height: 2,
  },
  legendText: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
  },
  plotViewport: {
    marginTop: 6,
    flex: 1,
    minHeight: 190,
  },
  plotWrap: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  yAxis: {
    width: 34,
    justifyContent: 'space-between',
    paddingRight: 6,
  },
  yTick: {
    fontSize: 10,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
    textAlign: 'right',
  },
  xRow: {
    marginTop: 6,
    marginLeft: 0,
    flexDirection: 'row',
    minHeight: 32,
    paddingBottom: 4,

  },
  xLabel: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#777777',
    textAlign: 'center',
  },
  xLabelSub: {
    fontSize: 10,
    lineHeight: 14,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#9CA3AF',
    textAlign: 'center',
  },
  xLabelCell: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  emptyText: {
    fontSize: 14,
    color: '#9CA3AF',
    fontWeight: '500',
  },
});

export default ExecutionTrendCard;
